import { supabase } from '../lib/supabase';

const organizationService = {
  async getOrgInfo(orgId) {
    const { data, error } = await supabase
      ?.from('organizations')
      ?.select('id, name, slug, address, phone, logo_url, rbq_number, tps_number, tvq_number, email, website, tagline')
      ?.eq('id', orgId)
      ?.single();
    return { data, error };
  },

  async updateOrgInfo(orgId, fields) {
    const allowed = ['address', 'phone', 'logo_url', 'rbq_number', 'tps_number', 'tvq_number', 'email', 'website', 'tagline'];
    const payload = {};
    for (const k of allowed) {
      if (k in fields) payload[k] = fields?.[k];
    }
    const { data, error } = await supabase
      ?.from('organizations')
      ?.update(payload)
      ?.eq('id', orgId)
      ?.select()
      ?.single();
    if (error) throw error;
    return { data, error: null };
  },

  async uploadLogo(orgId, file) {
    // Validate MIME type — SVG not supported by @react-pdf/renderer
    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes?.includes(file?.type)) {
      throw new Error('SVG_NOT_SUPPORTED');
    }

    // Validate file size (5 MB max)
    if (file?.size > 5242880) {
      throw new Error('FILE_TOO_LARGE');
    }

    // Compute extension from MIME type
    const extMap = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/webp': 'webp' };
    const ext = extMap?.[file?.type] || 'jpg';
    const path = `${orgId}/logo.${ext}`;

    // Upload to storage (upsert = overwrite existing)
    const { error: uploadError } = await supabase?.storage
      ?.from('organization-logos')
      ?.upload(path, file, { upsert: true, cacheControl: '3600', contentType: file?.type });

    if (uploadError) throw uploadError;

    // Get public URL
    const { data: urlData } = supabase?.storage
      ?.from('organization-logos')
      ?.getPublicUrl(path);

    // Save the storage path (not the URL) to organizations.logo_url
    await organizationService?.updateOrgInfo(orgId, { logo_url: path });

    // Return public URL with cache-bust
    const publicUrl = `${urlData?.publicUrl}?v=${Date.now()}`;
    return publicUrl;
  },

  async removeLogo(orgId) {
    // Try to delete from storage (best-effort, ignore error)
    try {
      // We need to find the current logo path first
      const { data: org } = await organizationService?.getOrgInfo(orgId);
      if (org?.logo_url) {
        await supabase?.storage
          ?.from('organization-logos')
          ?.remove([org?.logo_url]);
      }
    } catch {
      // best-effort, ignore
    }

    // Clear logo_url in DB
    await organizationService?.updateOrgInfo(orgId, { logo_url: null });
  },
};

export default organizationService;
